import React, {Component} from "react";
import PropTypes from "prop-types";

import {
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Input,
} from "reactstrap";

// Helper functions
import formatElement from "classes/FormatElement.jsx";

// core components
import Comments from "components/Features/Comments.jsx";
import ImageUploader from "components/Features/ImageUploader.jsx";
import SignatureField from "components/Features/SignatureField.jsx";
import BusinessField from "components/Features/BusinessField.jsx";
import AbnField from "components/Features/AbnField.jsx";
import CreditCardField from "components/Features/CreditCardField.jsx";
import AddressField from "components/Features/AddressField.jsx";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAt, faPhone, faUser, faMapMarkerAlt, faBuilding } from '@fortawesome/pro-solid-svg-icons';

class Field extends Component {
    
    debug = false;
    
    componentDidMount() {
        const { type, readonly, value } = this.props;
        if( 'tinymce' != type && 'sitereport' != type ){
            return;
        }
        if( readonly ){
            return;
        }
        this.setupQuill();
    }
    
    setupQuill = async () => {
        const { fldId, placeholder, onChange, value } = this.props;
        // we make a dynamic import for the QuillJS, as this component is not made to work on SSR
        const Quill = (await import("quill")).default;
        const quill = new Quill(document.querySelector('#meta_'+fldId), {
            modules: {
                toolbar: [
                    ["bold", "italic", 'underline'], // toggle buttons
                    [{list: "ordered"},{list: "bullet"}], // List
                    [{ 'indent': '-1'}, { 'indent': '+1' }], // outdent/indent
                    ['clean'], //clear formatting
                ],
            },
            placeholder: placeholder,
            theme: "snow"
        });
        const delta = quill.clipboard.convert(value);
        quill.setContents(delta, 'silent');
        
        quill.on('text-change', function(delta, oldDelta, source) {
            if (source == 'user') {
                const contents = quill.root.innerHTML;
                if( this.debug ) console.log("A user action triggered this change.",contents );
                onChange( contents );
            }
        });
    };
    
    render(){
        const {
            fldId,
            type,
            subType,
            options,
            value,
            placeholder,
            readonly,
            fldRequired,
            terms,
            step,
            min,
            max,
            maxlength,
            rows,
            error,
            helperText,
            errorText,
            onChange,
            logicFn,
            templateFn,
            className,
        } = this.props;
        
        const inputType = subType ?? type;
        let wrapperClass = className;
        let fldClass = '';
        if( error ){
            wrapperClass += ' fld-error';
            if(this.debug) console.log('FIELD ERROR!!!!!!!', fldId);
        }
        if( readonly ){
            fldClass += ' form-control-muted';
        }
        
        let parsedDesc = helperText ?? '';
        if( '' != parsedDesc ){
            parsedDesc = parsedDesc.replace(/\${_([\w_-]+)}/g, templateFn);
            if( this.debug ) console.log('Parsed Desc: ', parsedDesc);
        }
        
        let errorMsg = errorText ?? 'Field invalid. Please check your input.';
        
        let inputIcon = null;
        let inputField = null;
        switch(type){
            case 'text':
            case 'textarea':
            case 'number':
            case 'email':
            case 'phone':
                if( 'email' == type || 'email' == subType ){
                    inputIcon = <FontAwesomeIcon className="inputIcon" icon={faAt} />;
                } else if( 'phone' == type || 'phone' == subType ){
                    inputIcon = <FontAwesomeIcon className="inputIcon" icon={faPhone} />;
                } else if( 'name' == type || 'name' == subType ){
                    inputIcon = <FontAwesomeIcon className="inputIcon" icon={faUser} />;
                } else if( 'address' == type || 'address' == subType ){
                    inputIcon = <FontAwesomeIcon className="inputIcon" icon={faMapMarkerAlt} />;
                }
                const validTypes = ["button","textarea","checkbox","color","date","datetime-local","email","file","hidden","image","month","number","password","radio","range","reset","search","submit","tel","text","time","url","week"];
                let fldType = validTypes.includes(inputType) ? inputType : 'text';
                inputField = (
                    <Input
                        id={'meta_'+fldId}
                        type={fldType}
                        value={value}
                        placeholder={placeholder}
                        onChange={(e)=>{
                            let newStr = e.target.value;
                            if( 'bsb' === inputType ){
                                //console.log('Filtering BSB: ',newStr);
                                // Add dash after 3rd digit
                                const inputStr = newStr.trim(" ").replace(/\D/,'');
                                newStr = " ";
                                let len = inputStr.length;
                                for (let i = 0; i<len; i++) {
                                    newStr = newStr + inputStr[i];
                                    while(newStr.length % (3+1) === 0) {
                                        newStr = newStr + "-";
                                    }
                                }
                                
                                newStr = newStr.trim("-");
                                
                                if( newStr.length > 7 ){ // 2*3 digits, plus 1 dash
                                    newStr = newStr.substring(0, 7);
                                }
                                //console.log('Filtered: ',newStr);
                            }
                            onChange( newStr );
                        }}
                        invalid={error}
                        className={fldClass}
                        step={step}
                        min={min}
                        max={max}
                        maxlength={maxlength}
                        rows={rows}
                    />
                );
                break;
            case 'select':
                inputField = (
                    <Input
                        id={'meta_'+fldId}
                        type={inputType}
                        value={value}
                        placeholder={placeholder}
                        onChange={(e)=>{
                            onChange( e.target.value );
                        }}
                        className={fldClass}
                    >
                        <option key="dflt" value=''> -- SELECT -- </option>
                        {Object.keys(options).map((optKey, opt) => (
                            <option key={opt} value={optKey}>{options[optKey]}</option>
                        ))}
                    </Input>
                );
                break;
            case 'radio':
                fldClass += ' radio_container';
                inputField = (
                    <div id={'meta_'+fldId} className={fldClass}>
                        {Object.keys(options).map((optKey, opt) => {
                            const label = options[optKey].label ?? options[optKey];
                            const conditions = options[optKey].conditions ?? {};
                            if( logicFn( conditions ) ){
                                return (
                                    <div key={opt} className="custom-control custom-radio mb-3">
                                        <input
                                            className="custom-control-input"
                                            type="radio"
                                            id={fldId+'_'+optKey}
                                            name={fldId+'_'+optKey}
                                            checked={value==optKey}
                                            onChange={(e)=>{
                                                onChange( optKey );
                                            }}
                                        />
                                        <label className=" custom-control-label" htmlFor={fldId+'_'+optKey}>
                                            {label}
                                        </label>
                                    </div>
                                );
                            }
                        })}
                    </div>
                );
                break;
            case 'checkbox':
                fldClass += ' custom-control-input';
                
                return (
                    <InputGroup className={wrapperClass}>
                        <div className="custom-control custom-checkbox mb-3">
                            <input
                                id={'meta_'+fldId}
                                type="checkbox"
                                checked={value?'checked':''}
                                onChange={(e)=>{
                                    if( this.debug ) console.log('Checkbox change triggered', e);
                                    onChange( !value );
                                }}
                                className={fldClass}
                            />
                            <label className="custom-control-label" htmlFor={'meta_'+fldId}>
                                <span>
                                    {(helperText) ? parsedDesc : (null)}
                                    {(fldRequired) ? (<span className="fld-required">*</span>) : (null)}
                                </span>
                            </label>
                        </div>
                        {(error) ? (
                            <p className="fld-errormsg fld-description">{errorMsg}</p>
                        ) : (null)}
                    </InputGroup>
                );
                break;
            case 'comments':
                let prevComments = value ?? [];
                if( '' == prevComments ){
                    prevComments = [];
                }
                
                return (
                    <>
                        {(helperText) ? (
                            <p className="fld-description">{parsedDesc}</p>
                        ) : (null)}
                        <Comments fldId={'meta_'+fldId} comments={prevComments} />
                    </>
                );
                break;
            case 'instructions':
            case 'description':
                return (
                    <>
                        {(helperText) ? (
                            <p className="fld-description">{parsedDesc}</p>
                        ) : (null)}
                    </>
                );
                break;
            case 'html':
                let elements = [];
                let count = 0;
                terms.map((elm) => {
                    elements.push( formatElement(elm, count, logicFn, templateFn) );
                    count++;
                });
                
                return elements;
                break;
            case 'captioned_images':
                return (
                    <>
                        {(helperText) ? (
                            <p className="fld-description">{parsedDesc}</p>
                        ) : (null)}
                        <ImageUploader {...this.props} />
                        <br />
                        {(error) ? (
                            <p className="fld-errormsg fld-description">{errorMsg}</p>
                        ) : (null)}
                    </>
                );
                break;
            case 'signature':
                return (
                    <>
                        {(helperText) ? (
                            <p className="fld-description">{parsedDesc}</p>
                        ) : (null)}
                        <SignatureField {...this.props} />
                        {(error) ? (
                            <p className="fld-errormsg fld-description">{errorMsg}</p>
                        ) : (null)}
                    </>
                );
                break;
            case 'business':
                inputIcon = <FontAwesomeIcon className="inputIcon" icon={faBuilding} />;
                inputField = (
                    <BusinessField {...this.props}/>
                );
                break;
            case 'abn':
                inputIcon = <FontAwesomeIcon className="inputIcon" icon={faBuilding} />;
                inputField = (
                    <AbnField {...this.props}/>
                );
                break;
            case 'address':
                return (
                    <AddressField {...this.props}/>
                );
                break;
            case 'creditcard':
                return (
                    <>
                        {(helperText) ? (
                            <p className="fld-description">{parsedDesc}</p>
                        ) : (null)}
                        <CreditCardField {...this.props}/>
                        {(error) ? (
                            <p className="fld-errormsg fld-description">{errorMsg}</p>
                        ) : (null)}
                    </>
                );
                break;
            case 'document':
                if( this.debug ) console.log('Document field: ', value);
                let docurl = value.url ?? '';
                return (
                    <>
                        {(helperText) ? (
                            <p className="fld-description">{parsedDesc}</p>
                        ) : (null)}
                        {(''!=docurl) ? (
                            <a href={value.url} target="_blank" download={true}>View/Download: {value.name} [{value.size}]</a>
                        ) : (
                            <p>Document not found.</p>
                        )}
                        {(error) ? (
                            <p className="fld-errormsg fld-description">{errorMsg}</p>
                        ) : (null)}
                        
                    </>
                );
                break;
            default:
                console.warn('Field ignored due to unknown type: ',type);
                break;
        }
        
        return (
            <>
            {(helperText) ? (
                <p className="fld-description">{parsedDesc}</p>
            ) : (null)}
            <InputGroup className={wrapperClass}>
                {(null!=inputIcon) ? (
                    <InputGroupAddon addonType="prepend">
                        <InputGroupText className={fldClass}>{inputIcon}</InputGroupText>
                    </InputGroupAddon>
                ) : (null)}
                {inputField}
            </InputGroup>
            {(error) ? (
                <p className="fld-errormsg fld-description">{errorMsg}</p>
            ) : (null)}
            </>
        );
    }
}

Field.propTypes = {
    fldId: PropTypes.string,
    type: PropTypes.string,
    subType: PropTypes.string,
    options: PropTypes.object,
    map: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
    placeholder: PropTypes.string,
    readonly: PropTypes.bool,
    fldRequired: PropTypes.bool,
    terms: PropTypes.array,
    step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxlength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    error: PropTypes.bool,
    helperText: PropTypes.string,
    errorText: PropTypes.string,
    onChange: PropTypes.func,
    logicFn: PropTypes.func,
    templateFn: PropTypes.func,
    isVisible: PropTypes.bool,
    className: PropTypes.string,
};

Field.defaultProps = {
    fldId: null,
    type: 'text',
    subType: null,
    options: {},
    map: {},
    value: null,
    placeholder: null,
    readonly: null,
    fldRequired: false,
    terms: [],
    step: null,
    min: null,
    max: null,
    maxlength: null,
    rows: null,
    error: false,
    helperText: '',
    errorText: 'Field invalid. Please check your input.',
    onChange: (newVal) => {console.log('Field value updated',newVal)},
    logicFn: (conditions) => {return true;},
    templateFn: (match, fld) => {console.log('templateFn',match,fld);return match;},
    isVisible: false,
    className: "",
}

export default Field;
